import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import api, { bannersAPI, categoriesAPI } from '../services/api';
import { useToast } from '../components/Toast';
import {
  Plus, Edit2, Trash2, RefreshCw, X, Image as ImageIcon,
  ToggleLeft, ToggleRight, ChevronUp, ChevronDown,
} from 'lucide-react';
import './Banners.css';

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

// Background themes: 1-4 colours, drawn left-to-right as a gradient.
const THEMES = [
  { name: 'Green',       colors: ['#15803D', '#4ADE80'] },
  { name: 'Orange',      colors: ['#EA580C', '#FDBA74'] },
  { name: 'Purple',      colors: ['#6D28D9', '#C084FC'] },
  { name: 'Blue',        colors: ['#1D4ED8', '#60A5FA'] },
  { name: 'Rose',        colors: ['#BE123C', '#FB7185'] },
  { name: 'Teal',        colors: ['#0F766E', '#5EEAD4'] },
  { name: 'Amber',       colors: ['#B45309', '#FCD34D'] },
  { name: 'Charcoal',    colors: ['#111827', '#4B5563'] },
  { name: 'Aurora',      colors: ['#06B6D4', '#3B82F6', '#8B5CF6'] },
  { name: 'Sunset Pop',  colors: ['#F97316', '#EC4899', '#7C3AED'] },
  { name: 'Candy',       colors: ['#7C3AED', '#C026D3', '#F472B6'] },
  { name: 'Ocean Neon',  colors: ['#1E3A8A', '#2563EB', '#22D3EE'] },
  { name: 'Mango',       colors: ['#F59E0B', '#F97316', '#EF4444'] },
  { name: 'Mint Fresh',  colors: ['#059669', '#14B8A6', '#22D3EE'] },
  { name: 'Berry Blast', colors: ['#4C1D95', '#BE185D', '#FB7185'] },
  { name: 'Tropical',    colors: ['#0EA5E9', '#10B981', '#FACC15'] },
  { name: 'Lime Pop',    colors: ['#65A30D', '#22C55E', '#06B6D4'] },
  { name: 'Night Sky',   colors: ['#0F172A', '#1E3A8A', '#7C3AED'] },
  { name: 'Royal',       colors: ['#312E81', '#6D28D9', '#EC4899', '#FBBF24'] },
  { name: 'Festive',     colors: ['#7C2D12', '#DC2626', '#F97316', '#FACC15'] },
];

const MAX_STOPS = 4;

// One-tap solid colours for whichever colour box is selected.
const COLOR_PALETTE = [
  '#15803D', '#16A34A', '#22C55E', '#4ADE80', '#65A30D', '#A3E635', '#0F766E', '#14B8A6',
  '#06B6D4', '#22D3EE', '#0284C7', '#1D4ED8', '#3B82F6', '#60A5FA', '#4338CA', '#6D28D9',
  '#8B5CF6', '#C026D3', '#EC4899', '#F472B6', '#BE123C', '#EF4444', '#EA580C', '#F97316',
  '#F59E0B', '#FACC15', '#FDE68A', '#92400E', '#111827', '#4B5563', '#E5E7EB', '#FFFFFF',
];

const DECORATIONS = [
  { value: 'NONE',      label: 'None' },
  { value: 'STRIPES',   label: 'Stripes' },
  { value: 'BUBBLES',   label: 'Bubbles' },
  { value: 'WAVES',     label: 'Waves' },
  { value: 'GEOMETRIC', label: 'Geometric' },
  { value: 'DOTS',      label: 'Dots' },
];

// Shapes the photo can be cropped to (the customer app renders the same keys).
const SHAPES = [
  { value: 'CIRCLE',  label: 'Circle' },
  { value: 'ROUNDED', label: 'Rounded' },
  { value: 'LEAF',    label: 'Leaf' },
  { value: 'ARCH',    label: 'Arch' },
  { value: 'BLOB',    label: 'Blob' },
  { value: 'DIAMOND', label: 'Diamond' },
];

const GRADIENT_MODES = [
  { value: 'RING',    label: 'Ring' },
  { value: 'OVERLAY', label: 'Tint' },
  { value: 'NONE',    label: 'None' },
];

// One-tap gradients for the photo ring / tint.
const PHOTO_GRADIENTS = [
  { name: 'Gold',    a: '#FDE68A', b: '#F59E0B' },
  { name: 'Sunset',  a: '#F97316', b: '#EC4899' },
  { name: 'Ocean',   a: '#22D3EE', b: '#3B82F6' },
  { name: 'Berry',   a: '#A855F7', b: '#EC4899' },
  { name: 'Mint',    a: '#6EE7B7', b: '#22D3EE' },
  { name: 'Snow',    a: '#FFFFFF', b: '#BBF7D0' },
  { name: 'Rainbow', a: '#F472B6', m: '#FBBF24', b: '#34D399' },
  { name: 'Neon',    a: '#22D3EE', m: '#3B82F6', b: '#A855F7' },
  { name: 'Fire',    a: '#FDE047', m: '#F97316', b: '#DC2626' },
  { name: 'Candy',   a: '#FDE68A', m: '#F472B6', b: '#8B5CF6' },
];

const labelOf = (list, value) => list.find(x => x.value === value)?.label || value;

const TEXT_LIGHT = '#FFFFFF';
const TEXT_DARK  = '#111827';

const ACTIONS = [
  { value: 'NONE',     label: 'Nothing (display only)' },
  { value: 'CATEGORY', label: 'Open a category' },
  { value: 'PRODUCT',  label: 'Open a product' },
  { value: 'SEARCH',   label: 'Search for a keyword' },
];

const EMPTY_FORM = {
  label: '', title: '', subtitle: '', buttonText: 'Shop Now',
  actionType: 'NONE', actionValue: '',
  backgroundColors: [...THEMES[0].colors], decoration: 'STRIPES',
  textColor: TEXT_LIGHT, status: 'ACTIVE', startsAt: '', endsAt: '',
  imageShape: 'ROUNDED', imageGradientMode: 'RING',
  imageGradientStart: PHOTO_GRADIENTS[0].a, imageGradientMid: '', imageGradientEnd: PHOTO_GRADIENTS[0].b,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/** Valid colours for a banner: new list first, then the legacy start/end pair. */
function resolveColors(list, start, end) {
  const l = (Array.isArray(list) ? list : []).filter(c => HEX_RE.test(c || ''));
  if (l.length) return l;
  const out = [HEX_RE.test(start || '') ? start : THEMES[0].colors[0]];
  if (HEX_RE.test(end || '')) out.push(end);
  return out;
}

/** CSS background for 1 colour (solid) or 2-4 colours (gradient). */
function gradientCss(colors, deg = 110) {
  const v = colors.filter(c => HEX_RE.test(c || ''));
  if (!v.length) return THEMES[0].colors[0];
  if (v.length === 1) return v[0];
  return `linear-gradient(${deg}deg, ${v.join(', ')})`;
}

const sameColors = (a, b) => a.length === b.length && a.every((c, i) => c.toUpperCase() === (b[i] || '').toUpperCase());

/** ISO instant -> value for <input type="datetime-local"> in the admin's local time */
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value (local time) -> ISO instant the backend expects */
function toIso(local) {
  if (!local) return '';
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formFromBanner(b) {
  return {
    label: b.label || '',
    title: b.title || '',
    subtitle: b.subtitle || '',
    buttonText: b.buttonText || '',
    actionType: b.actionType || 'NONE',
    actionValue: b.actionValue || '',
    backgroundColors: resolveColors(b.backgroundColors, b.backgroundColor, b.backgroundColorEnd),
    decoration: b.decoration || 'NONE',
    textColor: b.textColor || TEXT_LIGHT,
    imageShape: b.imageShape || 'CIRCLE',
    imageGradientMode: b.imageGradientMode || 'RING',
    imageGradientStart: b.imageGradientStart || '',
    imageGradientMid: b.imageGradientMid || '',
    imageGradientEnd: b.imageGradientEnd || '',
    status: b.status || 'ACTIVE',
    startsAt: toLocalInput(b.startsAt),
    endsAt: toLocalInput(b.endsAt),
  };
}

/** Light text on dark gradient -> white button with coloured text; dark text -> dark button. */
function buttonColors(textColor, bg) {
  const lightText = textColor.toUpperCase() === TEXT_LIGHT;
  return { bg: lightText ? '#FFFFFF' : textColor, fg: lightText ? bg : '#FFFFFF' };
}

function bannerStatus(b) {
  if (b.status === 'INACTIVE') return { cls: 'inactive', text: 'Inactive' };
  if (b.live) return { cls: 'active', text: 'Live' };
  if (b.startsAt && new Date(b.startsAt) > new Date()) return { cls: 'pending', text: 'Scheduled' };
  return { cls: 'expired', text: 'Expired' };
}

function validate(form) {
  const errors = {};
  if (!form.title.trim()) errors.title = 'Title is required';
  if (form.actionType !== 'NONE' && !form.actionValue.trim()) {
    errors.actionValue = 'Choose what this banner should open';
  }
  if (!form.backgroundColors.length || form.backgroundColors.some(c => !HEX_RE.test(c))) {
    errors.backgroundColors = 'Every background colour must be a hex colour like #15803D';
  }
  if (!HEX_RE.test(form.textColor)) errors.textColor = 'Use a hex colour like #FFFFFF';
  if (form.imageGradientMid && !HEX_RE.test(form.imageGradientMid)) {
    errors.imageGradientMid = 'Use a hex colour like #F97316';
  }
  if (form.imageGradientStart && !HEX_RE.test(form.imageGradientStart)) {
    errors.imageGradientStart = 'Use a hex colour like #FDE68A';
  }
  if (form.imageGradientEnd && !HEX_RE.test(form.imageGradientEnd)) {
    errors.imageGradientEnd = 'Use a hex colour like #F59E0B';
  }
  if (form.startsAt && form.endsAt && new Date(form.endsAt) <= new Date(form.startsAt)) {
    errors.endsAt = 'End must be after the start';
  }
  return errors;
}

function actionSummary(b, categories, products) {
  switch (b.actionType) {
    case 'CATEGORY': {
      const c = categories.find(x => x.id === b.actionValue);
      return `Category: ${c ? c.name : 'unavailable'}`;
    }
    case 'PRODUCT': {
      const p = products.find(x => (x.id || x.productId) === b.actionValue);
      return `Product: ${p ? p.name : 'selected product'}`;
    }
    case 'SEARCH':
      return `Search: “${b.actionValue}”`;
    default:
      return 'No action';
  }
}

// ── Preview (mirrors the customer app card) ──────────────────────────────────

/** Decorative shapes in a 250 x 100 box (same artwork the customer app draws). */
function BannerDecor({ type }) {
  let shapes = null;
  switch (type) {
    case 'STRIPES':
      shapes = (
        <>
          <line x1="100" y1="64" x2="186" y2="0" stroke="#F472B6" strokeWidth="11" strokeLinecap="round" opacity="0.85" />
          <line x1="125" y1="92" x2="215" y2="24" stroke="#A78BFA" strokeWidth="11" strokeLinecap="round" opacity="0.8" />
          <line x1="150" y1="112" x2="250" y2="40" stroke="#34D399" strokeWidth="11" strokeLinecap="round" opacity="0.8" />
          <circle cx="112" cy="14" r="1.8" fill="#FFFFFF" />
          <circle cx="238" cy="92" r="1.4" fill="#FFFFFF" />
          <circle cx="30" cy="88" r="2" fill="#FFFFFF" opacity="0.7" />
        </>
      );
      break;
    case 'BUBBLES':
      shapes = (
        <>
          <circle cx="130" cy="4" r="17" fill="#FBBF24" opacity="0.8" />
          <circle cx="112" cy="82" r="8" fill="#22D3EE" opacity="0.85" />
          <circle cx="205" cy="74" r="11" fill="none" stroke="#FFFFFF" strokeWidth="3" opacity="0.35" />
          <circle cx="170" cy="20" r="2" fill="#FFFFFF" />
          <circle cx="96" cy="26" r="1.5" fill="#FFFFFF" />
          <circle cx="240" cy="14" r="2.5" fill="#F472B6" opacity="0.9" />
        </>
      );
      break;
    case 'WAVES':
      shapes = (
        <>
          <path d="M0 66 C40 44 80 92 125 66 S210 44 250 64 V100 H0 Z" fill="#FFFFFF" opacity="0.16" />
          <path d="M0 80 C50 58 90 100 140 78 S215 62 250 80 V100 H0 Z" fill="#FFFFFF" opacity="0.16" />
          <circle cx="130" cy="16" r="2" fill="#FFFFFF" opacity="0.8" />
          <circle cx="104" cy="40" r="1.4" fill="#FFFFFF" opacity="0.7" />
        </>
      );
      break;
    case 'GEOMETRIC':
      shapes = (
        <>
          <polygon points="108,10 121,34 95,34" fill="#FBBF24" opacity="0.85" />
          <polygon points="176,62 190,76 176,90 162,76" fill="#A78BFA" opacity="0.85" />
          <circle cx="96" cy="72" r="11" fill="none" stroke="#22D3EE" strokeWidth="3.5" opacity="0.7" />
          <circle cx="150" cy="14" r="2" fill="#FFFFFF" />
          <circle cx="224" cy="88" r="1.6" fill="#FFFFFF" />
        </>
      );
      break;
    case 'DOTS':
      shapes = (
        <>
          {Array.from({ length: 6 }).flatMap((_, r) => Array.from({ length: 9 }).map((__, c) => (
            <circle key={`${r}-${c}`} cx={105 + c * 14} cy={10 + r * 16} r="1.8" fill="#FFFFFF" opacity="0.25" />
          )))}
        </>
      );
      break;
    default:
      return null;
  }
  return (
    <svg className="bn-preview__decor" viewBox="0 0 250 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {shapes}
    </svg>
  );
}

BannerDecor.propTypes = { type: PropTypes.string };

function BannerPreview({ label, title, subtitle, buttonText, actionType, imageSrc,
  backgroundColors, backgroundColor, backgroundColorEnd, textColor, decoration,
  imageShape, imageGradientMode, imageGradientStart, imageGradientMid, imageGradientEnd }) {
  const colors = resolveColors(backgroundColors, backgroundColor, backgroundColorEnd);
  const bg = colors[0];
  const txt = HEX_RE.test(textColor) ? textColor : TEXT_LIGHT;
  const btn = buttonColors(txt, bg);
  const showButton = actionType !== 'NONE' && buttonText?.trim();
  const hasDecor = decoration && decoration !== 'NONE';

  // Photo styling — falls back to the original look (circle + soft white ring).
  const shape = (imageShape || 'CIRCLE').toLowerCase();
  const mode = (imageGradientMode || 'RING').toLowerCase();
  const g1 = HEX_RE.test(imageGradientStart || '') ? imageGradientStart : null;
  const g2 = HEX_RE.test(imageGradientEnd || '') ? imageGradientEnd : g1;
  const gm = g1 && HEX_RE.test(imageGradientMid || '') ? imageGradientMid : null;
  const photoGradient = g1 ? `linear-gradient(135deg, ${[g1, gm, g2].filter(Boolean).join(', ')})` : 'transparent';

  return (
    <div
      className={`bn-preview${imageSrc ? ' bn-preview--has-image' : ''}${showButton ? ' bn-preview--btn' : ''}`}
      style={{
        background: gradientCss(colors, 110),
        '--bn-from': bg, '--bn-to': colors[colors.length - 1], '--bn-text': txt,
        '--bn-btn-bg': btn.bg, '--bn-btn-fg': btn.fg,
      }}
    >
      {hasDecor ? <BannerDecor type={decoration} /> : <div className="bn-preview__glow" />}
      <div className="bn-preview__text">
        {label?.trim() && <div className="bn-preview__label">{label}</div>}
        <div className="bn-preview__title">{title?.trim() || 'Your banner title'}</div>
        {subtitle?.trim() && <div className="bn-preview__sub">{subtitle}</div>}
        {showButton && <span className="bn-preview__btn">{buttonText}</span>}
      </div>
      {imageSrc && (
        <div
          className={`bn-preview__img bn-shape--${shape} bn-gm--${g1 ? mode : 'none'}`}
          style={{ '--bn-grad': photoGradient }}
        >
          <div className="bn-preview__imgin"><img src={imageSrc} alt="" /></div>
        </div>
      )}
    </div>
  );
}

BannerPreview.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonText: PropTypes.string,
  actionType: PropTypes.string,
  imageSrc: PropTypes.string,
  backgroundColor: PropTypes.string,
  backgroundColorEnd: PropTypes.string,
  textColor: PropTypes.string,
  imageShape: PropTypes.string,
  imageGradientMode: PropTypes.string,
  imageGradientStart: PropTypes.string,
  imageGradientMid: PropTypes.string,
  imageGradientEnd: PropTypes.string,
  backgroundColors: PropTypes.array,
  decoration: PropTypes.string,
};

// ── Small form pieces ────────────────────────────────────────────────────────

function ColorField({ id, value, onChange }) {
  // Empty value (e.g. optional "Middle" colour) shows a neutral grey swatch, not black.
  const swatch = HEX_RE.test(value) ? value : (value ? '#000000' : '#E5E7EB');
  return (
    <div className="bn-color-field">
      <input
        type="color" aria-label="Pick colour"
        value={swatch}
        onChange={e => onChange(e.target.value.toUpperCase())}
      />
      <input
        id={id} type="text" value={value} maxLength={7} placeholder="#16A34A"
        onChange={e => onChange(e.target.value.toUpperCase())}
      />
    </div>
  );
}

ColorField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function ImagePicker({ src, fileName, onPick, onClear, error }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);

  const take = (file) => {
    if (file) onPick(file);
    if (ref.current) ref.current.value = '';
  };

  return (
    <div>
      {src ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 10, border: '2px solid var(--green)', background: 'rgba(34,197,94,0.05)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)' }}>
            <img src={src} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>✓ Image selected</p>
            {fileName && <p style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</p>}
            <button type="button" onClick={() => ref.current?.click()}
              style={{ marginTop: 4, fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              Change image
            </button>
          </div>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClear} title="Remove image"><X size={14} /></button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files[0]); }}
          style={{
            width: '100%', textAlign: 'center', cursor: 'pointer', padding: '24px 20px', borderRadius: 10,
            border: `2px dashed ${drag ? 'var(--primary)' : 'var(--border)'}`,
            background: drag ? 'var(--primary-glow-sm)' : 'var(--bg-hover)',
          }}
        >
          <ImageIcon size={26} style={{ color: 'var(--text-muted)', marginBottom: 6 }} />
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Click or drag &amp; drop a photo</p>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG, JPG, WEBP · Max 5 MB</p>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => take(e.target.files[0])} />
      <p className="bn-hint">
        Tip: use a <strong>square</strong> photo (about 800 × 800 px) with the subject in the centre —
        it is cropped to the shape you pick under Photo style and shown on the right of the banner.
      </p>
      {error && <p className="bn-err">⚠ {error}</p>}
    </div>
  );
}

ImagePicker.propTypes = {
  src: PropTypes.string,
  fileName: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  error: PropTypes.string,
};

function ActionTarget({ form, set, categories, products, productsLoading, error }) {
  if (form.actionType === 'CATEGORY') {
    const active = categories.filter(c => c.status === 'ACTIVE');
    return (
      <>
        <select value={form.actionValue} onChange={e => set({ actionValue: e.target.value })}>
          <option value="">— Select category —</option>
          {active.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {error && <p className="bn-err">⚠ {error}</p>}
      </>
    );
  }
  if (form.actionType === 'PRODUCT') {
    return (
      <>
        <select value={form.actionValue} onChange={e => set({ actionValue: e.target.value })} disabled={productsLoading}>
          <option value="">{productsLoading ? 'Loading products…' : '— Select product —'}</option>
          {products.map(p => {
            const id = p.id || p.productId;
            return <option key={id} value={id}>{p.name}</option>;
          })}
        </select>
        {error && <p className="bn-err">⚠ {error}</p>}
      </>
    );
  }
  if (form.actionType === 'SEARCH') {
    return (
      <>
        <input value={form.actionValue} maxLength={120} placeholder="e.g. chakli"
          onChange={e => set({ actionValue: e.target.value })} />
        {error && <p className="bn-err">⚠ {error}</p>}
      </>
    );
  }
  return <p className="bn-hint">The banner will only be displayed — the button is hidden.</p>;
}

ActionTarget.propTypes = {
  form: PropTypes.shape({ actionType: PropTypes.string, actionValue: PropTypes.string }).isRequired,
  set: PropTypes.func.isRequired,
  categories: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

// ── Create / edit modal ──────────────────────────────────────────────────────

function BannerModal({ banner, categories, products, productsLoading, onNeedProducts, onSave, onClose, saving }) {
  const isEdit = !!banner;
  const [form, setForm] = useState(() => (banner ? formFromBanner(banner) : EMPTY_FORM));
  const [activeStop, setActiveStop] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(banner?.image || '');
  const [removeImage, setRemoveImage] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const errors = validate(form);
  const hasErrors = Object.values(errors).some(Boolean);

  useEffect(() => {
    if (form.actionType === 'PRODUCT') onNeedProducts();
  }, [form.actionType, onNeedProducts]);

  const pickImage = (file) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > MAX_IMAGE_BYTES) {
      window.alert('Image is larger than 5 MB. Please choose a smaller one.');
      return;
    }
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onload = (e) => setImageSrc(e.target.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImageSrc('');
    setRemoveImage(isEdit && !!banner.image);
  };

  const setStop = (i, v) => setForm(f => ({
    ...f, backgroundColors: f.backgroundColors.map((c, idx) => (idx === i ? v : c)),
  }));
  const addStop = () => {
    if (form.backgroundColors.length >= MAX_STOPS) return;
    const last = form.backgroundColors[form.backgroundColors.length - 1] || THEMES[0].colors[0];
    set({ backgroundColors: [...form.backgroundColors, last] });
    setActiveStop(form.backgroundColors.length);
  };
  const removeStop = (i) => {
    if (form.backgroundColors.length <= 1) return;
    set({ backgroundColors: form.backgroundColors.filter((_, idx) => idx !== i) });
    setActiveStop(0);
  };
  const applyTheme = (t) => { set({ backgroundColors: [...t.colors] }); setActiveStop(0); };

  const applyPhotoGradient = (g) => set({
    imageGradientMode: form.imageGradientMode === 'NONE' ? 'RING' : form.imageGradientMode,
    imageGradientStart: g.a, imageGradientMid: g.m || '', imageGradientEnd: g.b,
  });

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) return;
    onSave({
      fields: {
        ...form,
        title: form.title.trim(),
        backgroundColor: form.backgroundColors[0],
        backgroundColorEnd: form.backgroundColors.length > 1 ? form.backgroundColors[form.backgroundColors.length - 1] : '',
        backgroundColors: form.backgroundColors.join(','),
        startsAt: toIso(form.startsAt),
        endsAt: toIso(form.endsAt),
      },
      imageFile,
      removeImage,
    });
  };

  const show = (key) => (submitted ? errors[key] : undefined);

  return (
    <div className="modal-overlay">
      <div className="modal bn-modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Banner' : 'New Banner'}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="bn-modal-preview">
          <BannerPreview {...form} imageSrc={imageSrc} />
          <small>Live preview — this is how it looks in the customer app</small>
        </div>

        <form onSubmit={submit} noValidate>
          <div className="bn-cols">

            {/* ── Column 1: Content + When tapped + Visibility ──── */}
            <div className="bn-col">
              <div className="bn-section-title">Content</div>

              <div className="form-group">
                <label htmlFor="bn-label">Small label <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <input id="bn-label" value={form.label} maxLength={40} placeholder="e.g. WELCOME OFFER"
                  onChange={e => set({ label: e.target.value })} />
              </div>

              <div className="form-group">
                <label htmlFor="bn-title">Title <span style={{ color: 'var(--red)' }}>*</span></label>
                <input id="bn-title" value={form.title} maxLength={60} placeholder="e.g. Fresh Homemade Items" autoFocus
                  onChange={e => set({ title: e.target.value })}
                  style={show('title') ? { borderColor: 'var(--red)' } : {}} />
                {show('title') && <p className="bn-err">⚠ {errors.title}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="bn-sub">Subtitle <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <input id="bn-sub" value={form.subtitle} maxLength={80} placeholder="e.g. Get 20% off on your first order"
                  onChange={e => set({ subtitle: e.target.value })} />
                <p className="bn-hint">Keep it short — with a button it shows on one line (about 35 characters).</p>
              </div>

              <div className="form-group">
                <label>Photo <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <ImagePicker src={imageSrc} fileName={imageFile?.name} onPick={pickImage} onClear={clearImage} />
              </div>

              <div className="bn-section-title">When tapped</div>

              <div className="bn-row2">
                <div className="form-group">
                  <label htmlFor="bn-action">Action</label>
                  <select id="bn-action" value={form.actionType}
                    onChange={e => set({ actionType: e.target.value, actionValue: '' })}>
                    {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="bn-btn">Button text</label>
                  <input id="bn-btn" value={form.buttonText} maxLength={24} placeholder="Shop Now"
                    disabled={form.actionType === 'NONE'}
                    onChange={e => set({ buttonText: e.target.value })} />
                </div>
              </div>

              {form.actionType !== 'NONE' && (
                <div className="form-group">
                  <label>
                    {{ CATEGORY: 'Category', PRODUCT: 'Product', SEARCH: 'Search keyword' }[form.actionType]}
                    {' '}<span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <ActionTarget form={form} set={set} categories={categories} products={products}
                    productsLoading={productsLoading} error={show('actionValue')} />
                </div>
              )}
              {form.actionType === 'NONE' && (
                <ActionTarget form={form} set={set} categories={categories} products={products}
                  productsLoading={productsLoading} />
              )}

              <div className="bn-section-title">Visibility</div>

              <div className="form-group">
                <label htmlFor="bn-status">Status</label>
                <select id="bn-status" value={form.status} onChange={e => set({ status: e.target.value })}>
                  <option value="ACTIVE">ACTIVE — visible to customers</option>
                  <option value="INACTIVE">INACTIVE — hidden</option>
                </select>
              </div>

              <div className="bn-row2">
                <div className="form-group">
                  <label htmlFor="bn-start">Show from <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <input id="bn-start" type="datetime-local" value={form.startsAt} onChange={e => set({ startsAt: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="bn-end">Hide after <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <input id="bn-end" type="datetime-local" value={form.endsAt} onChange={e => set({ endsAt: e.target.value })} />
                  {show('endsAt') && <p className="bn-err">⚠ {errors.endsAt}</p>}
                </div>
              </div>
              <p className="bn-hint">Leave both empty to keep the banner on until you turn it off.</p>
            </div>

            {/* ── Column 2: Background colours ──────────────────── */}
            <div className="bn-col">
              <div className="bn-section-title">Background colours</div>

              <div className="form-group">
                <label>Colour themes</label>
                <div className="bn-swatches">
                  {THEMES.map(t => (
                    <button
                      key={t.name} type="button" title={t.name} aria-label={t.name}
                      className={`bn-swatch bn-swatch--sm${sameColors(form.backgroundColors, t.colors) ? ' active' : ''}`}
                      style={{ background: gradientCss(t.colors, 135) }}
                      onClick={() => applyTheme(t)}
                    />
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>
                  Your colours <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(1 = solid, 2–{MAX_STOPS} = gradient)</span>
                </label>
                <div className="bn-stops">
                  {form.backgroundColors.map((c, i) => (
                    <div key={i} className={`bn-stop${activeStop === i ? ' active' : ''}`} onClick={() => setActiveStop(i)}>
                      <ColorField id={`bn-c${i}`} value={c} onChange={v => setStop(i, v)} />
                      {form.backgroundColors.length > 1 && (
                        <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label="Remove colour"
                          onClick={(e) => { e.stopPropagation(); removeStop(i); }}><X size={13} /></button>
                      )}
                    </div>
                  ))}
                </div>
                {form.backgroundColors.length < MAX_STOPS && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={addStop}><Plus size={13} /> Add colour</button>
                )}
                {show('backgroundColors') && <p className="bn-err">⚠ {errors.backgroundColors}</p>}
              </div>

              <div className="form-group">
                <label>Quick colours <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(sets colour {activeStop + 1})</span></label>
                <div className="bn-palette">
                  {COLOR_PALETTE.map(c => (
                    <button key={c} type="button" className="bn-dot" title={c} aria-label={c}
                      style={{ background: c }} onClick={() => setStop(activeStop, c)} />
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Text colour</label>
                <div className="bn-seg">
                  <button type="button" className={form.textColor === TEXT_LIGHT ? 'active' : ''}
                    onClick={() => set({ textColor: TEXT_LIGHT })}>White text</button>
                  <button type="button" className={form.textColor === TEXT_DARK ? 'active' : ''}
                    onClick={() => set({ textColor: TEXT_DARK })}>Dark text</button>
                </div>
                <div style={{ marginTop: 8 }}>
                  <ColorField id="bn-text" value={form.textColor} onChange={v => set({ textColor: v })} />
                </div>
                {show('textColor') && <p className="bn-err">⚠ {errors.textColor}</p>}
              </div>
            </div>

            {/* ── Column 3: Decoration + Photo style ────────────── */}
            <div className="bn-col">
              <div className="bn-section-title">Decoration</div>
              <div className="form-group">
                <div className="bn-shapes" role="radiogroup" aria-label="Decoration">
                  {DECORATIONS.map(d => (
                    <button
                      key={d.value} type="button" role="radio" aria-checked={form.decoration === d.value}
                      className={`bn-shape-btn${form.decoration === d.value ? ' active' : ''}`}
                      onClick={() => set({ decoration: d.value })}
                    >
                      <span className="bn-decor-thumb" style={{ background: gradientCss(form.backgroundColors, 110) }}>
                        <BannerDecor type={d.value} />
                      </span>
                      <span>{d.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bn-section-title">Photo style</div>

              <div className="form-group">
                <label>Photo shape</label>
                <div className="bn-shapes" role="radiogroup" aria-label="Photo shape">
                  {SHAPES.map(sh => (
                    <button
                      key={sh.value} type="button" role="radio" aria-checked={form.imageShape === sh.value}
                      className={`bn-shape-btn${form.imageShape === sh.value ? ' active' : ''}`}
                      onClick={() => set({ imageShape: sh.value })}
                    >
                      <span className={`bn-thumb bn-shape--${sh.value.toLowerCase()}`} />
                      <span>{sh.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Photo gradient</label>
                <div className="bn-seg">
                  {GRADIENT_MODES.map(m => (
                    <button key={m.value} type="button"
                      className={form.imageGradientMode === m.value ? 'active' : ''}
                      onClick={() => set({ imageGradientMode: m.value })}>{m.label}</button>
                  ))}
                </div>
                <p className="bn-hint">
                  <strong>Ring</strong> = coloured gradient border · <strong>Tint</strong> = soft gradient over the photo.
                </p>
              </div>

              {form.imageGradientMode !== 'NONE' && (
                <>
                  <div className="form-group">
                    <label>Gradient presets</label>
                    <div className="bn-swatches">
                      {PHOTO_GRADIENTS.map(g => (
                        <button
                          key={g.name} type="button" title={g.name} aria-label={g.name}
                          className={`bn-swatch bn-swatch--sm${form.imageGradientStart === g.a && form.imageGradientEnd === g.b && (form.imageGradientMid || '') === (g.m || '') ? ' active' : ''}`}
                          style={{ background: gradientCss([g.a, g.m, g.b].filter(Boolean), 135) }}
                          onClick={() => applyPhotoGradient(g)}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="bn-row3">
                    <div className="form-group">
                      <label htmlFor="bn-g1">Start</label>
                      <ColorField id="bn-g1" value={form.imageGradientStart} onChange={v => set({ imageGradientStart: v })} />
                      {show('imageGradientStart') && <p className="bn-err">⚠ {errors.imageGradientStart}</p>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="bn-gm">Middle</label>
                      <ColorField id="bn-gm" value={form.imageGradientMid} onChange={v => set({ imageGradientMid: v })} />
                      {show('imageGradientMid') && <p className="bn-err">⚠ {errors.imageGradientMid}</p>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="bn-g2">End</label>
                      <ColorField id="bn-g2" value={form.imageGradientEnd} onChange={v => set({ imageGradientEnd: v })} />
                      {show('imageGradientEnd') && <p className="bn-err">⚠ {errors.imageGradientEnd}</p>}
                    </div>
                  </div>
                  <p className="bn-hint">Middle is optional — leave it empty for a 2-colour gradient.</p>
                  {!imageSrc && <p className="bn-hint">Add a photo to see the shape and gradient in the preview.</p>}
                </>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Banner')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

BannerModal.propTypes = {
  banner: PropTypes.object,
  categories: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  onNeedProducts: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

// ── Delete modal ─────────────────────────────────────────────────────────────

function DeleteModal({ banner, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Delete Banner</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Delete <strong style={{ color: 'var(--text-primary)' }}>“{banner.title}”</strong>? It will disappear
          from the customer app and can’t be restored. To hide it temporarily, set it to Inactive instead.
        </p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>{loading ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

DeleteModal.propTypes = {
  banner: PropTypes.shape({ title: PropTypes.string }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── Card ─────────────────────────────────────────────────────────────────────

function BannerCard({ banner, index, count, categories, products, busy, onMove, onToggle, onEdit, onDelete }) {
  const st = bannerStatus(banner);
  const isActive = banner.status === 'ACTIVE';
  const hasAction = banner.actionType && banner.actionType !== 'NONE';
  const dash = '—';
  const cardColors = resolveColors(banner.backgroundColors, banner.backgroundColor, banner.backgroundColorEnd);
  const schedule = (() => {
    if (!banner.startsAt && !banner.endsAt) return 'Always on';
    return `${banner.startsAt ? fmtDate(banner.startsAt) : 'Now'} → ${banner.endsAt ? fmtDate(banner.endsAt) : 'No end'}`;
  })();

  const details = [
    { k: 'Title',       v: banner.title || dash },
    { k: 'Label',       v: banner.label || dash },
    { k: 'Subtitle',    v: banner.subtitle || dash },
    { k: 'Button text', v: hasAction ? (banner.buttonText || dash) : 'Hidden (no action)' },
    { k: 'Action',      v: actionSummary(banner, categories, products) },
    { k: 'Photo',       v: banner.image
        ? `${labelOf(SHAPES, banner.imageShape || 'CIRCLE')} · gradient ${labelOf(GRADIENT_MODES, banner.imageGradientMode || 'RING').toLowerCase()}`
        : 'No' },
    { k: 'Decoration',  v: labelOf(DECORATIONS, banner.decoration || 'NONE') },
    { k: 'Status',      v: banner.status || dash },
    { k: 'Schedule',    v: schedule },
  ];

  return (
    <div className={`card bn-card${isActive ? '' : ' bn-card--off'}`}>
      <BannerPreview {...banner} imageSrc={banner.image || ''} />

      <div className="bn-meta">
        <div className="bn-meta-row">
          <span className="bn-order" title="Position in the app carousel">#{index + 1}</span>
          <span className={`badge ${st.cls}`}>{st.text}</span>
        </div>

        <dl className="bn-details">
          {details.map(d => (
            <div key={d.k} className="bn-details-row">
              <dt>{d.k}</dt>
              <dd title={String(d.v)}>{d.v}</dd>
            </div>
          ))}

          <div className="bn-details-row">
            <dt>Colours</dt>
            <dd className="bn-colors">
              {cardColors.map(c => (
                <span key={c} className="bn-chip" style={{ background: c }} title={c} />
              ))}
              <span className="bn-chip" style={{ background: banner.textColor }} title={`Text ${banner.textColor}`} />
              <small>{cardColors.join(' → ')} · text {banner.textColor}</small>
            </dd>
          </div>
        </dl>
      </div>

      <div className="bn-actions">
        <button className="btn btn-ghost btn-icon btn-sm" disabled={busy || index === 0} onClick={() => onMove(banner.id, -1)} title="Move earlier" aria-label="Move earlier"><ChevronUp size={15} /></button>
        <button className="btn btn-ghost btn-icon btn-sm" disabled={busy || index === count - 1} onClick={() => onMove(banner.id, 1)} title="Move later" aria-label="Move later"><ChevronDown size={15} /></button>
        <button className="btn btn-ghost btn-sm bn-toggle" disabled={busy} onClick={() => onToggle(banner)}>
          {isActive
            ? <><ToggleRight size={14} style={{ color: 'var(--green)' }} /> Active</>
            : <><ToggleLeft size={14} style={{ color: 'var(--text-muted)' }} /> Inactive</>}
        </button>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEdit(banner)} title="Edit" aria-label="Edit"><Edit2 size={14} /></button>
        <button className="btn btn-danger btn-icon btn-sm" onClick={() => onDelete(banner)} title="Delete" aria-label="Delete"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

BannerCard.propTypes = {
  banner: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  categories: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  busy: PropTypes.bool.isRequired,
  onMove: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Banners() {
  const toast = useToast();
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const productsRequested = useRef(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit' | 'delete', banner? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, cRes] = await Promise.allSettled([
        bannersAPI.getAll(),
        categoriesAPI.getAll(0, 200, null),
      ]);
      if (bRes.status !== 'fulfilled') {
        toast('Failed to load banners', 'error');
        return;
      }
      setBanners(bRes.value.data?.data || []);
      if (cRes.status === 'fulfilled') {
        setCategories(cRes.value.data?.data || cRes.value.data?.content || []);
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Products are only needed for "Open a product" banners — fetch once, on demand.
  const ensureProducts = useCallback(async () => {
    if (productsRequested.current) return;
    productsRequested.current = true;
    setProductsLoading(true);
    try {
      const res = await api.get('/products', { params: { page: 0, size: 1000 } });
      setProducts(res.data?.content || res.data || []);
    } catch {
      productsRequested.current = false;
      toast('Could not load products', 'error');
    } finally {
      setProductsLoading(false);
    }
  }, [toast]);

  // If any banner already targets a product, load names so the cards can show them.
  useEffect(() => {
    if (banners.some(b => b.actionType === 'PRODUCT')) ensureProducts();
  }, [banners, ensureProducts]);

  const errMsg = (err, fallback) => err.response?.data?.message || fallback;

  const handleSave = async ({ fields, imageFile, removeImage }) => {
    setBusy(true);
    try {
      if (modal.mode === 'create') {
        await bannersAPI.create(fields, imageFile);
        toast('Banner created', 'success');
      } else {
        await bannersAPI.update(modal.banner.id, fields, imageFile, removeImage);
        toast('Banner updated', 'success');
      }
      setModal(null);
      load();
    } catch (err) {
      toast(errMsg(err, `Failed to ${modal.mode} banner`), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await bannersAPI.delete(modal.banner.id);
      toast('Banner deleted', 'info');
      setModal(null);
      load();
    } catch (err) {
      toast(errMsg(err, 'Failed to delete banner'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (banner) => {
    const status = banner.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    setBusy(true);
    try {
      await bannersAPI.setStatus(banner.id, status);
      toast(`“${banner.title}” is now ${status.toLowerCase()}`, 'success');
      load();
    } catch (err) {
      toast(errMsg(err, 'Failed to update status'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async (id, dir) => {
    const from = banners.findIndex(b => b.id === id);
    const to = from + dir;
    if (from < 0 || to < 0 || to >= banners.length) return;

    const next = [...banners];
    [next[from], next[to]] = [next[to], next[from]];
    setBanners(next);                       // optimistic
    setBusy(true);
    try {
      await bannersAPI.reorder(next.map(b => b.id));
    } catch (err) {
      toast(errMsg(err, 'Failed to save order'), 'error');
      load();                                // roll back to server state
    } finally {
      setBusy(false);
    }
  };

  const stats = useMemo(() => ({
    total: banners.length,
    live: banners.filter(b => b.live).length,
    hidden: banners.filter(b => !b.live).length,
  }), [banners]);

  return (
    <Layout title="Banners" subtitle="Promotional banners shown on the customer home screen">
      <div className="cat-topbar">
        <div className="cat-topbar-row1" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={load} aria-label="Refresh"><RefreshCw size={13} /></button>
          <button className="btn btn-primary btn-sm" onClick={() => setModal({ mode: 'create' })}>
            <Plus size={15} /> New Banner
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: stats.total, color: 'var(--text-primary)' },
          { label: 'Live in app', value: stats.live, color: 'var(--green)' },
          { label: 'Hidden', value: stats.hidden, color: 'var(--text-muted)' },
        ].map(s => (
          <div key={s.label} style={{ padding: '10px 18px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {loading && <div className="page-loader"><div className="spinner" /></div>}

      {!loading && banners.length === 0 && (
        <div className="empty-state" style={{ marginTop: 60 }}>
          <ImageIcon size={48} />
          <h3>No banners yet</h3>
          <p>Create your first banner — it will appear at the top of the customer app.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModal({ mode: 'create' })}>
            <Plus size={14} /> Create Banner
          </button>
        </div>
      )}

      {!loading && banners.length > 0 && (
        <>
          <p className="bn-hint" style={{ marginBottom: 14 }}>
            Customers see <strong>live</strong> banners in the order shown here (#1 first) and they auto-scroll.
            Use the arrows to change the order.
          </p>
          <div className="bn-grid">
            {banners.map((b, i) => (
              <BannerCard
                key={b.id} banner={b} index={i} count={banners.length}
                categories={categories} products={products} busy={busy}
                onMove={handleMove} onToggle={handleToggle}
                onEdit={x => setModal({ mode: 'edit', banner: x })}
                onDelete={x => setModal({ mode: 'delete', banner: x })}
              />
            ))}
          </div>
        </>
      )}

      {(modal?.mode === 'create' || modal?.mode === 'edit') && (
        <BannerModal
          banner={modal.mode === 'edit' ? modal.banner : null}
          categories={categories} products={products} productsLoading={productsLoading}
          onNeedProducts={ensureProducts}
          onSave={handleSave} onClose={() => setModal(null)} saving={busy}
        />
      )}
      {modal?.mode === 'delete' && (
        <DeleteModal banner={modal.banner} onConfirm={handleDelete} onClose={() => setModal(null)} loading={busy} />
      )}
    </Layout>
  );
}